/**
 * Cliente HTTP das fotos do signatário (E80.F1, ADR-0079) — raia eletrônica.
 *
 * Espelha apps/api/types/fotos_do_signatario.py e
 * apps/api/runtime/fotos_do_signatario.py. Dois grupos por caso
 * (`documento_identidade` e `ccb`), cada um com várias fotos e **uma escolhida**
 * — trocar a escolhida é um clique e não apaga nada.
 *
 * O recorte feito na tela é GEOMETRIA: a caixa e o giro viajam para o servidor,
 * e o arquivo original continua intacto lá (§2.2). Por isso existe a versão
 * `original` do blob: é ela que permite conferir se o recorte foi honesto.
 */
import type { AxiosInstance } from 'axios';

import { classifyExameEletronicoError } from '@/lib/exame_eletronico';

/** Onde a foto foi encontrada, na linguagem do dossiê. */
export type GrupoDaFoto = 'documento_identidade' | 'ccb';

/** Como a imagem efetiva foi produzida; `recorte_automatico` desde a E131.F1 (ADR-0079 E.25). */
export type OrigemDaFoto = 'anexo_direto' | 'recorte_na_tela' | 'recorte_automatico';

export type RotacaoDaFoto = 0 | 90 | 180 | 270;

/** E141.F1 (ADR-0079 E.26): por que a leitura automática não produziu caixa (os motivos da E.25
 * e a falha do provedor). Espelha `MotivoDaLeituraDoRecorte`. */
export type MotivoDaLeituraDoRecorte =
  | 'sem_fotografia'
  | 'sem_caixa'
  | 'caixa_fora_da_imagem'
  | 'caixa_pequena'
  | 'caixa_quase_inteira'
  | 'proporcao_implausivel'
  | 'falha_da_leitura';

/**
 * E141.F1 (ADR-0079 E.26): o que a leitura automática do recorte deixou na foto. Espelha
 * `LeituraDoRecorte`. `motivo` sem `ajustada_em` é o recorte pendente: a foto entrou inteira na
 * conferência e o perito ainda não ajustou.
 */
export interface LeituraDoRecorteData {
  lido_em: string;
  leitor: string;
  versao_do_pedido: string;
  recortada: boolean;
  rotacao_deg: RotacaoDaFoto;
  motivo: MotivoDaLeituraDoRecorte | null;
  ajustada_em: string | null;
}

/**
 * E191.F1 (GT-2026-0030): a ordem das telas é a do painel da conferência — a fotografia da
 * formalização primeiro, a do documento de identidade depois.
 */
export const GRUPOS_DA_FOTO: readonly GrupoDaFoto[] = ['ccb', 'documento_identidade'];

export interface CaixaDoRecorte {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FotoDoSignatarioData {
  id: string;
  case_id: string;
  usuario_id: string;
  grupo: GrupoDaFoto;
  eleita: boolean;
  nome_arquivo: string;
  mime_type: string;
  size_bytes: number;
  content_sha256: string;
  bounding_box: CaixaDoRecorte | null;
  rotacao_deg: RotacaoDaFoto;
  origem: OrigemDaFoto;
  /**
   * E87.F2: de qual documento do caso a imagem saiu, e em que página; `null` na foto anexada à mão.
   * E123.F3: é a origem que a pré-análise mostra ao lado da fotografia escolhida.
   */
  proveniencia?: { documento_id: string; pagina: number | null } | null;
  /** E122.F2/E123.F2 (ADR-0079 E.23/E.24): escolhida pelo sistema, e não pelo perito. */
  confirmacao_automatica?: boolean;
  /** E141.F1 (E.26): a leitura automática do recorte; ausente na foto nunca lida. */
  recorte_automatico?: LeituraDoRecorteData | null;
  created_at: string;
}

export interface GrupoDeFotosData {
  grupo: GrupoDaFoto;
  fotos: FotoDoSignatarioData[];
  /** A foto que entra na conferência; `null` enquanto ninguém escolheu. */
  eleita_id: string | null;
}

export interface FotosDoSignatarioData {
  case_id: string;
  grupos: GrupoDeFotosData[];
}

export interface RecorteInput {
  /** `null` desfaz o recorte e devolve a foto inteira. */
  bounding_box: CaixaDoRecorte | null;
  rotacao_deg: RotacaoDaFoto;
}

function basePath(caseId: string): string {
  return `/casos/${caseId}/fotos-do-signatario`;
}

/**
 * Resposta que não é o contrato vira erro, e não um objeto meio-vazio.
 *
 * Sem esta guarda, um corpo inesperado (um 204 sem corpo, uma resposta de outra
 * rota) chegava à tela como `data` sem `grupos`, e o `.map` derrubava a PÁGINA
 * inteira — a seção das fotos levava junto o protocolo e a tabela de peças.
 * Falha de leitura tem de caber dentro da própria seção.
 */
function ehContrato(data: unknown): data is FotosDoSignatarioData {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { grupos?: unknown }).grupos)
  );
}

export async function listarFotosDoSignatario(
  client: AxiosInstance,
  caseId: string,
): Promise<FotosDoSignatarioData> {
  let data: unknown;
  try {
    ({ data } = await client.get<FotosDoSignatarioData>(basePath(caseId)));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehContrato(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

/** Anexa uma foto ao grupo. A primeira de um grupo vazio já vem escolhida. */
export async function anexarFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  grupo: GrupoDaFoto,
  arquivo: File,
): Promise<FotoDoSignatarioData> {
  const form = new FormData();
  form.append('file', arquivo);
  form.append('grupo', grupo);
  try {
    const { data } = await client.post<FotoDoSignatarioData>(basePath(caseId), form);
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/** Troca a foto do grupo que entra na conferência. Nada é apagado. */
export async function elegerFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
): Promise<FotoDoSignatarioData> {
  try {
    const { data } = await client.post<FotoDoSignatarioData>(
      `${basePath(caseId)}/${fotoId}/eleger`,
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * E131.F1.C01 (homologação de 29/10, GT-2026-0289): o recorte grava a mesma linha que a análise
 * fotográfica e o processamento do envio gravam, e espera a transação deles terminar. Com os 15 s do
 * cliente, a tela dizia que não salvou um recorte que o servidor salvou; o mesmo teto da conclusão.
 */
export const RECORTE_TIMEOUT_MS = 10 * 60_000;

/** Grava a caixa e o giro do recorte; o arquivo original não é tocado. */
export async function recortarFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
  recorte: RecorteInput,
): Promise<FotoDoSignatarioData> {
  try {
    const { data } = await client.patch<FotoDoSignatarioData>(
      `${basePath(caseId)}/${fotoId}/recorte`,
      recorte,
      { timeout: RECORTE_TIMEOUT_MS },
    );
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

export async function removerFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
): Promise<void> {
  try {
    await client.delete(`${basePath(caseId)}/${fotoId}`);
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * Baixa os bytes da foto para exibir na tela.
 *
 * `versao: 'original'` traz exatamente o arquivo que subiu — é o que o editor
 * de recorte precisa, porque desenhar a caixa sobre a imagem já recortada
 * mediria a caixa contra o recorte anterior, e não contra a foto.
 */
export async function baixarFotoDoSignatario(
  client: AxiosInstance,
  caseId: string,
  fotoId: string,
  versao: 'efetiva' | 'original' = 'efetiva',
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(`${basePath(caseId)}/${fotoId}/blob`, {
      params: { versao },
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
